import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Spinner } from 'flowbite-react';
import { HiExternalLink, HiSearch } from 'react-icons/hi';
import { PageHeader } from '../components/common/PageHeader';
import { LogoCandidateGrid, OrganizationLogo } from '../components/organization';
import { useAuth } from '../contexts/AuthContext';
import { getUserErrorMessage, leasApi } from '../api';
import { LOGO_ACCEPT, logoFileProblem } from '../utils/organizationLogo';
import { runWithConcurrency } from '../utils/runWithConcurrency';
import type { LEAListItem, LogoCandidate, LogoCandidateList } from '../types/api';

type Filter = 'missing' | 'hasLogo' | 'all';

interface RowState {
  searching?: boolean;
  found?: LogoCandidateList;
  savingUrl?: string | null;
  savedUrl?: string | null;
  error?: string | null;
  message?: string | null;
  /** Saved during this visit: stays listed under "Missing" until the page reloads */
  kept?: boolean;
}

/** Websites searched at once; each search can take up to ~20 seconds */
const SEARCH_CONCURRENCY = 3;

const siteLabel = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');
const siteHref = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/**
 * Give every organization a logo: search each website for candidates and
 * pick one, upload a file, or paste an image URL. Super admins only.
 * See docs/features/ORGANIZATION_LOGOS.md.
 */
export const OrganizationLogosPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<LEAListItem[]>([]);
  const [rows, setRows] = useState<Record<number, RowState>>({});
  const [filter, setFilter] = useState<Filter>('missing');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bulk, setBulk] = useState<{ done: number; total: number } | null>(null);
  const stopRequested = useRef(false);

  useEffect(() => {
    if (currentUser && currentUser.role !== 'superAdmin') {
      navigate('/access-denied');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    leasApi.list({ limit: 500 })
      .then((list) => { setOrganizations(list); setError(null); })
      .catch((err) => setError(getUserErrorMessage(err, 'Failed to load organizations')))
      .finally(() => setLoading(false));
  }, []);

  const updateRow = (leaId: number, change: Partial<RowState>) =>
    setRows((current) => ({ ...current, [leaId]: { ...current[leaId], ...change } }));

  const counts = useMemo(() => {
    const withLogo = organizations.filter((o) => o.logoUpdatedAt).length;
    return { missing: organizations.length - withLogo, hasLogo: withLogo, all: organizations.length };
  }, [organizations]);

  const visible = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return organizations.filter((o) => {
      if (filter === 'missing' && o.logoUpdatedAt && !rows[o.leaId]?.kept) return false;
      if (filter === 'hasLogo' && !o.logoUpdatedAt) return false;
      const text = `${o.nameOfInstitution} ${o.shortNameOfInstitution ?? ''} ${o.webSite ?? ''}`.toLowerCase();
      return words.every((w) => text.includes(w));
    });
  }, [organizations, rows, filter, search]);

  const searchable = visible.filter((o) => o.webSite && !rows[o.leaId]?.found && !rows[o.leaId]?.searching);

  const searchWebsite = async (org: LEAListItem) => {
    updateRow(org.leaId, { searching: true, error: null, message: null });
    try {
      const found = await leasApi.getLogoCandidates(org.leaId);
      updateRow(org.leaId, { searching: false, found });
    } catch (err) {
      console.error('[OrganizationLogosPage] Website search failed', { leaId: org.leaId, err });
      updateRow(org.leaId, { searching: false, error: getUserErrorMessage(err, 'The website could not be searched.') });
    }
  };

  const searchAll = async () => {
    const targets = searchable;
    stopRequested.current = false;
    setBulk({ done: 0, total: targets.length });
    await runWithConcurrency(targets, SEARCH_CONCURRENCY, async (org) => {
      await searchWebsite(org);
      setBulk((b) => (b ? { ...b, done: b.done + 1 } : b));
    }, () => stopRequested.current);
    setBulk(null);
  };

  const saved = (org: LEAListItem, logoUpdatedAt: string, change: Partial<RowState>) => {
    setOrganizations((list) => list.map((o) => (o.leaId === org.leaId ? { ...o, logoUpdatedAt } : o)));
    updateRow(org.leaId, { error: null, kept: true, ...change });
  };

  const chooseCandidate = async (org: LEAListItem, candidate: LogoCandidate) => {
    updateRow(org.leaId, { savingUrl: candidate.url, error: null, message: null });
    try {
      const logo = await leasApi.setLogoFromUrl(org.leaId, candidate.url);
      saved(org, logo.logoUpdatedAt, { savingUrl: null, savedUrl: candidate.url, message: 'Logo saved.' });
    } catch (err) {
      console.error('[OrganizationLogosPage] Saving logo failed', { leaId: org.leaId, err });
      updateRow(org.leaId, { savingUrl: null, error: getUserErrorMessage(err, 'That image could not be saved.') });
    }
  };

  const chooseUrl = async (org: LEAListItem, url: string) => {
    updateRow(org.leaId, { savingUrl: url, error: null, message: null });
    try {
      const logo = await leasApi.setLogoFromUrl(org.leaId, url);
      saved(org, logo.logoUpdatedAt, { savingUrl: null, savedUrl: null, message: 'Logo saved from the URL.' });
    } catch (err) {
      console.error('[OrganizationLogosPage] Saving logo from URL failed', { leaId: org.leaId, err });
      updateRow(org.leaId, { savingUrl: null, error: getUserErrorMessage(err, 'That image could not be saved.') });
    }
  };

  const uploadFile = async (org: LEAListItem, file: File) => {
    const problem = logoFileProblem(file);
    if (problem) {
      updateRow(org.leaId, { error: problem, message: null });
      return;
    }
    updateRow(org.leaId, { savingUrl: 'upload', error: null, message: null });
    try {
      const logo = await leasApi.uploadLogo(org.leaId, file);
      saved(org, logo.logoUpdatedAt, { savingUrl: null, savedUrl: null, message: 'Logo uploaded.' });
    } catch (err) {
      console.error('[OrganizationLogosPage] Upload failed', { leaId: org.leaId, err });
      updateRow(org.leaId, { savingUrl: null, error: getUserErrorMessage(err, 'The logo could not be uploaded.') });
    }
  };

  const filterButton = (value: Filter, label: string) => (
    <button
      type="button"
      onClick={() => setFilter(value)}
      className={`rounded-full px-3 py-1 text-sm font-medium ${
        filter === value
          ? 'bg-blue-600 text-white'
          : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-600 dark:hover:bg-gray-700'
      }`}
      aria-pressed={filter === value}
    >
      {label} ({counts[value]})
    </button>
  );

  return (
    <div className="container mx-auto p-8">
      <PageHeader
        title="Organization Logos"
        subtitle="Find each organization's logo on its website, upload one, or paste an image URL. Choices save right away."
        breadcrumbs={[{ label: 'Admin' }, { label: 'Organization Logos' }]}
      />

      {error && (
        <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        {filterButton('missing', 'Missing')}
        {filterButton('hasLogo', 'Has a logo')}
        {filterButton('all', 'All')}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name or website"
          aria-label="Filter organizations"
          className="w-64 max-w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
        <div className="ml-auto flex items-center gap-3">
          {bulk ? (
            <>
              <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Spinner size="sm" /> Searched {bulk.done} of {bulk.total}…
              </span>
              <button
                type="button"
                onClick={() => { stopRequested.current = true; }}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Stop
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={searchAll}
              disabled={searchable.length === 0}
              className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <HiSearch className="h-4 w-4" />
              Search {searchable.length} website{searchable.length === 1 ? '' : 's'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : visible.length === 0 ? (
        <p className="rounded-lg bg-white p-6 text-center text-gray-600 shadow dark:bg-gray-800 dark:text-gray-300">
          {filter === 'missing' && !search ? 'Every organization has a logo.' : 'No organizations match.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {visible.map((org) => (
            <OrganizationRow
              key={org.leaId}
              org={org}
              state={rows[org.leaId] ?? {}}
              onSearch={() => searchWebsite(org)}
              onUseCandidate={(candidate) => chooseCandidate(org, candidate)}
              onUseUrl={(url) => chooseUrl(org, url)}
              onUpload={(file) => uploadFile(org, file)}
              onDismiss={() => updateRow(org.leaId, { error: null, message: null })}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

interface OrganizationRowProps {
  org: LEAListItem;
  state: RowState;
  onSearch: () => void;
  onUseCandidate: (candidate: LogoCandidate) => void;
  onUseUrl: (url: string) => void;
  onUpload: (file: File) => void;
  onDismiss: () => void;
}

const OrganizationRow: React.FC<OrganizationRowProps> = ({
  org, state, onSearch, onUseCandidate, onUseUrl, onUpload, onDismiss,
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');
  const busy = !!state.savingUrl || !!state.searching;

  return (
    <li className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
      <div className="flex flex-wrap items-start gap-4">
        <OrganizationLogo leaId={org.leaId} name={org.nameOfInstitution} logoUpdatedAt={org.logoUpdatedAt} size="lg" />
        <div className="min-w-0 flex-1">
          <Link to={`/leas/${org.leaId}`} className="font-semibold text-gray-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400">
            {org.nameOfInstitution}
          </Link>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {org.webSite ? (
              <a href={siteHref(org.webSite)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
                {siteLabel(org.webSite)} <HiExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : (
              'No website on file'
            )}
            {' · '}
            {org.logoUpdatedAt ? 'Has a logo' : 'No logo yet'}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {org.webSite && (
            <button
              type="button"
              onClick={onSearch}
              disabled={busy}
              className="rounded border border-blue-600 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-400 dark:text-blue-300 dark:hover:bg-gray-700"
            >
              {state.searching ? 'Searching…' : state.found ? 'Search again' : 'Search website'}
            </button>
          )}
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={busy}
            className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {state.savingUrl === 'upload' ? 'Uploading…' : 'Upload file'}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept={LOGO_ACCEPT}
            className="hidden"
            aria-label={`Choose a logo image for ${org.nameOfInstitution}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) onUpload(file);
            }}
          />
        </div>
      </div>

      {state.error && (
        <Alert color="failure" onDismiss={onDismiss} className="mt-3">
          <span className="text-sm">{state.error}</span>
        </Alert>
      )}
      {state.message && (
        <Alert color="success" onDismiss={onDismiss} className="mt-3">
          <span className="text-sm">{state.message}</span>
        </Alert>
      )}

      {state.searching && (
        <p className="mt-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <Spinner size="sm" /> Looking for logos on {org.webSite ? siteLabel(org.webSite) : 'the website'}…
        </p>
      )}
      {state.found && !state.searching && (
        <div className="mt-3">
          {state.found.candidates.length > 0 ? (
            <LogoCandidateGrid
              candidates={state.found.candidates}
              onUse={onUseCandidate}
              savingUrl={state.savingUrl}
              savedUrl={state.savedUrl}
            />
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Nothing usable found{state.found.problem ? ` (${state.found.problem})` : ''}. Upload a file or paste an image URL.
            </p>
          )}
        </div>
      )}

      <form
        className="mt-3 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (url.trim()) onUseUrl(url.trim());
        }}
      >
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Or paste an image URL"
          aria-label={`Image URL for ${org.nameOfInstitution}'s logo`}
          className="w-96 max-w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
        />
        <button
          type="submit"
          disabled={busy || !url.trim()}
          className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          {state.savingUrl && state.savingUrl === url.trim() ? 'Saving…' : 'Use URL'}
        </button>
      </form>
    </li>
  );
};
